import { useEffect, useMemo, useState } from "react"
import { IconCopy, IconRefresh } from "@tabler/icons-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { useHasOpenBenefitEditors } from "@/components/benefit/benefit-edit-scope"
import { isUiBuildStale, loadedUiBuild } from "@/lib/build-diagnostics"
import { api } from "@/lib/api"

type ApiVersion = { environment?: string; version?: string | null; fileVersion?: string | null; commit?: string | null }
type UiVersion = { environment?: string; version?: string | null; commit?: string | null }
const loadedUi = loadedUiBuild()

function valueOrUnknown(value: string | null | undefined) {
  return value || "unknown"
}

export function BuildDiagnostics() {
  const [apiVersion, setApiVersion] = useState<ApiVersion | null>(null)
  const [deployedUi, setDeployedUi] = useState<UiVersion | null>(null)
  const hasOpenEditors = useHasOpenBenefitEditors()

  useEffect(() => {
    let active = true
    api.get<ApiVersion>("/api/version")
      .then(value => { if (active) setApiVersion(value) })
      .catch(() => { if (active) setApiVersion(null) })
    const checkUi = () => {
      fetch("/ui-version.json", { cache: "no-store" })
        .then(response => response.ok ? response.json() as Promise<UiVersion> : Promise.reject(new Error("metadata unavailable")))
        .then(value => { if (active) setDeployedUi(value) })
        .catch(() => { if (active) setDeployedUi(null) })
    }
    checkUi()
    window.addEventListener("focus", checkUi)
    return () => { active = false; window.removeEventListener("focus", checkUi) }
  }, [])

  const diagnostics = useMemo(() => JSON.stringify({
    ui: {
      loadedVersion: loadedUi.version,
      loadedCommit: loadedUi.commit,
      loadedEnvironment: loadedUi.environment,
      deployedVersion: deployedUi?.version ?? null,
      deployedCommit: deployedUi?.commit ?? null,
      deployedEnvironment: deployedUi?.environment ?? null,
    },
    api: apiVersion,
  }, null, 2), [apiVersion, deployedUi])

  const refresh = () => {
    if (hasOpenEditors) {
      toast.error("Save or cancel the open editor before refreshing.")
      return
    }
    window.location.reload()
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(diagnostics)
      toast.success("Build diagnostics copied.")
    } catch {
      toast.error("The browser did not allow copying diagnostics.")
    }
  }

  return (
    <details className="px-3 py-2 text-xs text-muted-foreground">
      <summary className="cursor-pointer select-none">About and build diagnostics</summary>
      <div className="mt-2 space-y-1">
        {isUiBuildStale(loadedUi, deployedUi) && <p className="font-medium text-amber-700">A newer WebUI build is available. Save or cancel any open editor before refreshing.</p>}
        <div>Loaded UI: {valueOrUnknown(loadedUi.version)} ({valueOrUnknown(loadedUi.commit)})</div>
        <div>Current UI metadata: {valueOrUnknown(deployedUi?.version)} ({valueOrUnknown(deployedUi?.commit)})</div>
        <div>API: {valueOrUnknown(apiVersion?.version)} ({valueOrUnknown(apiVersion?.commit)})</div>
        <div>Environment: {valueOrUnknown(apiVersion?.environment ?? deployedUi?.environment ?? loadedUi.environment)}</div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => void copy()}>
            <IconCopy className="mr-1 size-3.5" /> Copy diagnostics
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2" disabled={hasOpenEditors} onClick={refresh}>
            <IconRefresh className="mr-1 size-3.5" /> Refresh safely
          </Button>
        </div>
      </div>
    </details>
  )
}
